/**
 * WidgetStoresContext - Provides widget-specific stores for isolation
 *
 * This context solves the multi-widget problem by providing each widget instance
 * with its own isolated set of Zustand stores. This prevents state collisions when
 * multiple widgets are embedded on the same page.
 *
 * Usage:
 * ```tsx
 * <WidgetStoresProvider widgetId="my-widget">
 *   <RefineVoiceEditor {...props} />
 *   <WordAlternatives />
 * </WidgetStoresProvider>
 * ```
 */

import { createContext, type ReactNode, useContext, useMemo } from 'react';
import { createWidgetStores, type WidgetStores } from '../store/createWidgetStores';

const WidgetStoresContext = createContext<WidgetStores | null>(null);

interface WidgetStoresProviderProps {
  widgetId: string;
  children: ReactNode;
}

/**
 * Provider component that creates and provides widget-specific stores
 */
export function WidgetStoresProvider({ widgetId, children }: WidgetStoresProviderProps) {
  // Create stores once per widgetId using useMemo to ensure stability
  const stores = useMemo(() => createWidgetStores(widgetId), [widgetId]);

  return <WidgetStoresContext.Provider value={stores}>{children}</WidgetStoresContext.Provider>;
}

/**
 * Hook to access widget-specific stores from context
 * @throws Error if used outside WidgetStoresProvider
 */
export function useWidgetStores(): WidgetStores {
  const context = useContext(WidgetStoresContext);

  if (!context) {
    throw new Error(
      'useWidgetStores must be used within a WidgetStoresProvider. ' +
        'Wrap your component tree with <WidgetStoresProvider widgetId="...">'
    );
  }

  return context;
}

/**
 * Convenience hooks for accessing individual widget stores
 */
export function useWidgetTranscriptStore() {
  const { useTranscriptStore } = useWidgetStores();
  return useTranscriptStore();
}

export function useWidgetRecordingStore() {
  const { useRecordingStore } = useWidgetStores();
  return useRecordingStore();
}

export function useWidgetRerecordStore() {
  const { useRerecordStore } = useWidgetStores();
  return useRerecordStore();
}

export function useWidgetRefinementStore() {
  const { useRefinementStore } = useWidgetStores();
  return useRefinementStore();
}

export function useWidgetAppStateStore() {
  const { useAppStateStore } = useWidgetStores();
  return useAppStateStore();
}

export function useWidgetConnectionStore() {
  const { useConnectionStore } = useWidgetStores();
  return useConnectionStore();
}
